import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText, ArrowRight, X, Sparkles } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CABINET_BORDEAUX = '#8B0028';
const CABINET_IVORY = '#FBF8F3';
const CABINET_BORDEAUX_DARK = '#6E001F';

/** Formate "2026-04" → "Avril 2026". */
export function formatPeriodLabel(period) {
  if (!period || !/^\d{4}-\d{2}$/.test(period)) return period || '';
  const [year, month] = period.split('-');
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
  ];
  const idx = parseInt(month, 10) - 1;
  return `${months[idx] || month} ${year}`;
}

/** Hook unique qui pilote toute la logique "Rapport du mois" (état + actions). */
export function useCabinetReport(user) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { available, report, consulted, toast_due }

  const isEligible = user?.plan === 'pilotage' || user?.plan === 'premium';

  const refresh = useCallback(async () => {
    if (!isEligible) { setLoading(false); return; }
    const token = localStorage.getItem('benchory_token');
    if (!token) { setLoading(false); return; }
    try {
      const r = await fetch(`${API_URL}/client/monthly-reports/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await r.json();
      setData(json);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isEligible]);

  const markToastSeen = useCallback(async () => {
    if (!data?.report?.period) return;
    const token = localStorage.getItem('benchory_token');
    if (!token) return;
    try {
      await fetch(`${API_URL}/client/monthly-reports/${data.report.period}/mark-toast-seen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((prev) => prev ? { ...prev, toast_due: false } : prev);
    } catch (_e) { /* best-effort */ }
  }, [data]);

  useEffect(() => { refresh(); }, [refresh]);

  return {
    loading,
    isEligible,
    available: !!data?.available,
    period: data?.report?.period,
    consulted: !!data?.consulted,
    toastDue: !!data?.toast_due,
    indice: data?.report?.indice_benchory,
    refresh,
    markToastSeen,
  };
}

/** Badge dynamique à droite du libellé "Rapport du mois". */
export function CabinetReportBadge({ state, period }) {
  // state ∈ "nouveau" | "consulte" | "attente"
  if (state === 'nouveau') {
    return (
      <span
        className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
        style={{ backgroundColor: CABINET_BORDEAUX, fontFamily: "'Playfair Display', serif", letterSpacing: '0.12em' }}
        data-testid="cabinet-badge-new"
      >
        Nouveau
      </span>
    );
  }
  if (state === 'consulte') {
    return (
      <span
        className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-gray-500 bg-gray-100"
        data-testid="cabinet-badge-period"
      >
        {formatPeriodLabel(period)}
      </span>
    );
  }
  if (state === 'attente') {
    return (
      <span
        className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-gray-400 bg-gray-50 border border-gray-200"
        data-testid="cabinet-badge-pending"
      >
        En attente
      </span>
    );
  }
  return null;
}

/** Bandeau premium affiché en haut du Dashboard quand un nouveau rapport est dispo. */
export function CabinetReportBanner({ period, indice, onOpen }) {
  return (
    <div
      className="mb-6 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm"
      style={{
        backgroundColor: CABINET_IVORY,
        borderLeft: `4px solid ${CABINET_BORDEAUX}`,
        border: '1px solid rgba(139, 0, 40, 0.12)',
      }}
      data-testid="cabinet-report-banner"
    >
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <div
          className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full flex-shrink-0"
          style={{ backgroundColor: 'rgba(139, 0, 40, 0.08)' }}
        >
          <ScrollText size={22} style={{ color: CABINET_BORDEAUX }} strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p
            className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1"
            style={{ color: CABINET_BORDEAUX }}
          >
            Cabinet Benchory · Conseil stratégique
          </p>
          <h3
            className="text-lg sm:text-xl font-medium leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#2A1419' }}
          >
            Votre rapport mensuel de {formatPeriodLabel(period).toLowerCase()} vous attend
          </h3>
          {typeof indice === 'number' && (
            <p className="text-xs text-gray-500 mt-1.5">
              Indice Benchory du mois&nbsp;: <strong style={{ color: CABINET_BORDEAUX }}>{indice.toFixed(1)}/100</strong>
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onOpen}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-md flex-shrink-0 whitespace-nowrap"
        style={{ backgroundColor: CABINET_BORDEAUX, fontFamily: 'inherit' }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = CABINET_BORDEAUX_DARK)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = CABINET_BORDEAUX)}
        data-testid="cabinet-banner-open-btn"
      >
        Ouvrir le rapport <ArrowRight size={16} />
      </button>
    </div>
  );
}

/** Toast discret en haut à droite — affiché au premier login après publication. */
export function CabinetReportToast({ period, onOpen, onDismiss }) {
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setClosing(true);
      setTimeout(onDismiss, 300);
    }, 8000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div
      className="fixed top-5 right-5 z-50 w-[340px] max-w-[calc(100vw-2.5rem)] rounded-xl shadow-xl overflow-hidden"
      style={{
        backgroundColor: 'white',
        borderTop: `3px solid ${CABINET_BORDEAUX}`,
        opacity: closing ? 0 : 1,
        transform: closing ? 'translateY(-12px)' : 'translateY(0)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
      }}
      data-testid="cabinet-report-toast"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className="flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0"
            style={{ backgroundColor: 'rgba(139, 0, 40, 0.10)' }}
          >
            <Sparkles size={16} style={{ color: CABINET_BORDEAUX }} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-[9px] uppercase tracking-[0.18em] font-semibold mb-0.5"
              style={{ color: CABINET_BORDEAUX }}
            >
              Le Cabinet Benchory
            </p>
            <p
              className="text-sm font-medium leading-snug"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#2A1419' }}
            >
              Votre rapport de {formatPeriodLabel(period).toLowerCase()} est disponible
            </p>
            <button
              onClick={onOpen}
              className="mt-2.5 text-xs font-semibold transition-colors"
              style={{ color: CABINET_BORDEAUX }}
              onMouseEnter={(e) => (e.currentTarget.style.color = CABINET_BORDEAUX_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.color = CABINET_BORDEAUX)}
              data-testid="cabinet-toast-open-btn"
            >
              Consulter →
            </button>
          </div>
          <button
            onClick={() => { setClosing(true); setTimeout(onDismiss, 300); }}
            className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
            aria-label="Fermer"
            data-testid="cabinet-toast-dismiss-btn"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Mention discrète d'upgrade pour les clients Diagnostic. */
export function CabinetUpgradeHint({ onContact }) {
  return (
    <div
      className="mb-6 rounded-xl p-4 flex items-center gap-3 text-xs"
      style={{
        backgroundColor: '#FAFAF8',
        border: '1px dashed rgba(139, 0, 40, 0.20)',
        color: '#5C2A30',
      }}
      data-testid="cabinet-upgrade-hint"
    >
      <ScrollText size={16} style={{ color: CABINET_BORDEAUX, opacity: 0.7 }} strokeWidth={1.5} />
      <span className="flex-1">
        <strong style={{ color: CABINET_BORDEAUX, fontFamily: "'Playfair Display', serif" }}>Le Cabinet Benchory</strong>
        {' '}vous accompagne chaque mois avec un rapport stratégique complet — disponible sur les offres Pilotage et Premium.
      </span>
      {onContact && (
        <button
          onClick={onContact}
          className="font-semibold whitespace-nowrap transition-colors"
          style={{ color: CABINET_BORDEAUX }}
          data-testid="cabinet-upgrade-cta"
        >
          En savoir plus →
        </button>
      )}
    </div>
  );
}
